import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ShoppingCart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KasszaKosar = () => {
  const [enabled, setEnabled] = useState(false);
  const [cart, setCart] = useState(null);
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const adTimerRef = useRef(null);

  // Fetch cart data every 2 seconds
  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await axios.get(`${API}/kassza/display/cart`);
        setEnabled(res.data.enabled);
        setCart(res.data.cart);
        if (res.data.ads) {
          setAds(res.data.ads);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch cart', err);
        setLoading(false);
      }
    };

    fetchCart();
    const interval = setInterval(fetchCart, 2000);
    return () => clearInterval(interval);
  }, []);

  // Ad rotation timer
  useEffect(() => {
    if (ads.length === 0) return;

    const currentAd = ads[currentAdIndex];
    const duration = (currentAd?.duration || 5) * 1000;

    adTimerRef.current = setTimeout(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, duration);

    return () => {
      if (adTimerRef.current) {
        clearTimeout(adTimerRef.current);
      }
    };
  }, [currentAdIndex, ads]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart size={80} className="text-slate-600 mx-auto mb-4" />
          <h1 className="text-2xl text-slate-400 font-semibold">Kassza kosár kijelző</h1>
          <p className="text-slate-500 mt-2">Jelenleg nem elérhető</p>
        </div>
      </div>
    );
  }

  const currentAd = ads[currentAdIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex">
      {/* Left side - Ads */}
      <div className="w-1/2 flex items-center justify-center p-8 relative overflow-hidden">
        {ads.length > 0 && currentAd ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={currentAd.url}
              alt="Reklám"
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
              style={{ maxHeight: 'calc(100vh - 4rem)' }}
            />
            {/* Ad indicators */}
            {ads.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {ads.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentAdIndex ? 'bg-emerald-500 scale-125' : 'bg-slate-600'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="w-32 h-32 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-6xl">🐴</span>
            </div>
            <p className="text-slate-400 text-xl">Gepárd Lovas Egyesület</p>
          </div>
        )}
      </div>

      {/* Right side - Cart */}
      <div className="w-1/2 bg-slate-800/50 backdrop-blur p-8 flex flex-col">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
            <ShoppingCart size={32} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Kosár</h1>
            {cart?.cashier_name && (
              <p className="text-slate-400">Kasszás: {cart.cashier_name}</p>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto">
          {cart && cart.items && cart.items.length > 0 ? (
            <div className="space-y-4">
              {cart.items.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-slate-700/50 rounded-2xl p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{item.qty}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">{item.name}</h3>
                      <p className="text-slate-400">{item.price?.toLocaleString('hu-HU')} Ft / db</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-400">
                      {(item.price * item.qty).toLocaleString('hu-HU')} Ft
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart size={80} className="text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 text-xl">A kosár üres</p>
                <p className="text-slate-600 text-sm mt-2">Várakozás a tételekre...</p>
              </div>
            </div>
          )}
        </div>

        {/* Total */}
        {cart && cart.items && cart.items.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-600">
            <div className="flex items-center justify-between">
              <span className="text-2xl text-slate-300">Összesen:</span>
              <span className="text-5xl font-bold text-emerald-400">
                {cart.total?.toLocaleString('hu-HU')} Ft
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KasszaKosar;
